import type { ReactElement } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SwapOutlined } from '@ant-design/icons';
import { Radio, Skeleton } from 'antd';
import { useTranslation } from '@govard/i18n-core/react';
import { EntityHistoryFeed } from '../../../components/entity-detail/EntityHistoryFeed.js';
import type { CompanyHistoryItem } from '../hooks/useCompanyHistory.js';

const FILTERS = ['all', 'deals', 'contacts', 'tasks', 'notes'] as const;
type CompanyHistoryFilter = (typeof FILTERS)[number];

function matches(item: CompanyHistoryItem, filter: CompanyHistoryFilter): boolean {
  if (filter === 'deals') return item.kind.startsWith('deal_');
  if (filter === 'contacts')
    return item.kind.startsWith('contact_') || item.kind === 'interaction_logged';
  if (filter === 'tasks') return item.kind.startsWith('task_');
  if (filter === 'notes') return item.kind.startsWith('note_');
  return true;
}

export function companyHistoryFeedItem(item: CompanyHistoryItem) {
  return {
    id: item.id,
    date: item.date,
    text: item.text,
    meta: item.actor,
    icon: item.kind === 'deal_stage_changed' ? <SwapOutlined /> : undefined,
  };
}

export function CompanyHistoryTab({
  items,
  loading,
  error,
  onRetry,
}: {
  items: CompanyHistoryItem[];
  loading: boolean;
  error: Error | null;
  onRetry: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const requested = params.get('historyFilter');
  const filter: CompanyHistoryFilter = FILTERS.find((value) => value === requested) ?? FILTERS[0];
  return (
    <>
      <div className="entity-list-toolbar">
        <Radio.Group
          optionType="button"
          buttonStyle="solid"
          size="small"
          value={filter}
          onChange={(event) => {
            const next = new URLSearchParams(params);
            next.set('historyFilter', String(event.target.value));
            setParams(next, { replace: true });
          }}
          options={FILTERS.map((value) => ({
            value,
            label: t(`crm.features.company_detail.history_${value}`),
          }))}
        />
      </div>
      {loading ? (
        <Skeleton active />
      ) : error ? (
        <ErrorState title={t('crm.entity_detail.load_error')} onRetry={onRetry} />
      ) : (
        <EntityHistoryFeed
          items={items.filter((item) => matches(item, filter)).map(companyHistoryFeedItem)}
        />
      )}
    </>
  );
}
import { ErrorState } from '../../../shared/ui/ErrorState.js';
